/** @format */

// The Contact support drawer and its floating button. The page talks to it
// through window.supportBox, which common.js stubs with a queue so calls made
// before the entry loads aren't lost:
//   ('set', {...})          merge into the metadata sent with a ticket
//   ('showButton', bool)    show or hide the floating button
//   ('showForm', true)      open the drawer
import {ChatAltIcon} from '@heroicons/react/outline'
import React, {useEffect, useState} from 'react'

import ContactSupport from './ContactSupport'

import './contact-support.css'

// What the floating button looks like:
//   bubble  Formbutton's round message bubble, same size and spot
//   text    a pill reading "Contact support"
export default function SupportBox({
  trigger = 'bubble',
}: {
  trigger?: 'bubble' | 'text'
}) {
  const [metadata, setMetadata] = useState<Record<string, string | number>>({})
  const [showButton, setShowButton] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    function supportBox(...[command, value]: SupportBoxCommand) {
      if (command === 'set') {
        const defined = Object.entries(value).filter(
          (entry): entry is [string, string | number] => entry[1] !== undefined,
        )
        setMetadata(m => ({...m, ...Object.fromEntries(defined)}))
      } else if (command === 'showButton') {
        setShowButton(value)
      } else if (value) {
        window.dispatchEvent(new CustomEvent('open-contact-support'))
      }
    }
    window.supportBox?.q?.forEach(args => supportBox(...args))
    window.supportBox = supportBox
  }, [])

  const {
    SERVICE_URL,
    FORMBUTTON_HASHID,
    FORMBUTTON_RESPONSE_TIME,
    HELP_ASK_URL,
  } = window.formspree

  return (
    <>
      <button
        type="button"
        // Hidden rather than unmounted: the drawer hands focus back to this
        // element when it closes.
        hidden={!showButton || drawerOpen}
        onClick={e =>
          window.dispatchEvent(
            new CustomEvent('open-contact-support', {
              detail: {trigger: e.currentTarget},
            }),
          )
        }
        aria-label="Contact support"
        className={
          trigger === 'bubble'
            ? 'fixed bottom-[22px] right-[22px] z-40 h-[60px] w-[60px] rounded-full bg-[#c4001a] p-3 shadow-[inset_0_1px_2px_0_rgba(255,255,255,.5),inset_0_-4px_8px_0_rgba(0,0,0,.1),0.3em_0.3em_1.4em_rgba(22,22,22,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300'
            : 'fixed bottom-6 right-6 z-40 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300'
        }
      >
        {trigger === 'bubble' ? (
          <svg
            viewBox="0 0 6.35 7.4083"
            className="h-full w-full fill-white"
            aria-hidden="true"
          >
            <path d="m 1.0834321,0.1292394 c -0.5491197,0 -0.9913472,0.4417175 -0.9913472,0.9908372 v 4.0128403 c 0,0.5491201 0.4422275,0.9913477 0.9913472,0.9913477 H 4.1773753 C 4.3297414,7.0476198 5.3858089,7.3434351 6.2442561,7.3390859 5.4678746,6.9530202 5.3218667,6.3864112 5.8172061,5.9681383 5.8166321,5.9678514 5.8162418,5.9673831 5.8156561,5.9671283 6.0908672,5.7913804 6.273829,5.4850425 6.273829,5.132927 V 1.1200863 c 0,-0.5491199 -0.4422277,-0.9908375 -0.9913476,-0.9908375 z" />
          </svg>
        ) : (
          // The flex layout sits on this span because `flex` on the button
          // would override the `hidden` attribute.
          <span className="flex items-center gap-2">
            <ChatAltIcon className="h-5 w-5" aria-hidden="true" />
            Contact support
          </span>
        )}
      </button>
      <ContactSupport
        askUrl={HELP_ASK_URL}
        ticketUrl={`${SERVICE_URL}/${FORMBUTTON_HASHID}`}
        responseDays={Number(FORMBUTTON_RESPONSE_TIME)}
        metadata={metadata}
        sourceLayout="uncategorized"
        onOpenChange={setDrawerOpen}
      />
    </>
  )
}
